import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { LOCAL_DB_SCHEMA } from '../schema'
import { ROLE_NO, type RoleCode, WRITE_ROLES } from '../seed/platform.seed'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, idOf, roleCodesOf } from './c07.shared'

/**
 * Helper bersama Master Data (MST-01/03/03A/06/07/08/09/10/11): hak tulis per page (sama dengan matriks capability
 * platform), jejak audit `tx_log` (`log_type` "Master Data"), hitung pemakaian dari relasi skema, serta aktif/nonaktif
 * dan hapus permanen yang seragam di semua master.
 */

export type MasterWh = 'RM' | 'FG'
export const MASTER_LOG_TYPE = 'Master Data'

export const num = (value: unknown) => Number(value ?? 0)
export const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
export const text = (value: unknown) => String(value ?? '').trim()
export const sameName = (a: unknown, b: unknown) => text(a).toLowerCase() === text(b).toLowerCase()
export const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

/** Kode peran persona aktif. */
export const myRoles = () => roleCodesOf(currentUserId())

/** Hak tulis halaman = capability platform (`WRITE_ROLES`), jadi tidak ada dua sumber kebenaran. */
export function canWrite(pageCode: string, roles: string[] = myRoles()): boolean {
  const allowed = WRITE_ROLES[pageCode] ?? []
  return roles.some(code => allowed.includes(ROLE_NO[code as RoleCode]))
}

export function requireWrite(pageCode: string, message: string) {
  if (!canWrite(pageCode)) fail(message, 'ERR_UNAUTHORIZED_ROLE', 403)
}

const warehouseIdOf = (wh: MasterWh | null) =>
  wh ? (readTable('mt_warehouses').find(row => row.code === `WH-${wh}`)?.id ?? null) : null

/** Satu baris jejak di `tx_log` (tampil di INV-02 Riwayat Mutasi Stok). */
export function masterLog(
  wh: MasterWh | null,
  pageCode: string,
  action: string,
  docRef: string,
  detail: string,
  logType: string = MASTER_LOG_TYPE
) {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    log_type: logType,
    page_code: pageCode,
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: warehouseIdOf(wh),
    user_id: currentUserId()
  })
}

/* ------------------------------------------------------------------ pemakaian */

/** Nama ramah collection yang menunjuk ke master (untuk kalimat "Dipakai di: …"). */
const USAGE_LABEL: Record<string, string> = {
  mt_material: 'material',
  mt_material_pack_standard: 'kemasan supplier',
  mt_bill_of_material: 'baris BOM',
  rm_arrival_line: 'baris kedatangan',
  purchase_request_line: 'baris PR',
  material_request_line: 'baris request material',
  transfer_line: 'baris transfer',
  return_order_line: 'baris retur',
  rm_unit: 'unit stok RM',
  rm_ng_item: 'barang NG RM',
  rm_opening_stock: 'baris pendataan awal',
  fg_labeler_batch: 'batch FG',
  fg_label: 'label stok',
  fg_pack_stock: 'stok pack',
  fg_ng_pack: 'pack NG',
  delivery_plan_line: 'baris order pengiriman',
  claim_item: 'barang claim',
  claim: 'claim',
  fg_picking_task_item: 'baris picking FG',
  fg_putaway_task_item: 'baris putaway FG',
  fg_rts_task_item: 'baris return-to-stock',
  fg_unpack_order: 'pengajuan bongkar kemasan',
  merge_request: 'permintaan penggabungan',
  merge_request_item: 'baris penggabungan',
  picking_task: 'tugas picking',
  fg_putaway_task: 'tugas putaway FG',
  consolidation_task_item: 'baris konsolidasi',
  fg_staging: 'staging FG',
  pallet: 'pallet',
  container: 'container',
  mt_rack: 'rak',
  mt_rack_bin: 'lokasi rak',
  rm_putaway_task_item: 'baris putaway RM',
  picking_task_item: 'baris picking RM',
  opname_scan: 'hasil pindai opname',
  rm_move_task_item: 'baris tugas pindah',
  initial_capture_area: 'rack pendataan awal',
  initial_capture_scan: 'hasil scan pendataan awal',
  discrepancy: 'selisih opname',
  ng_ticket: 'tiket NG',
  handover: 'serah terima',
  material_request: 'request material',
  fg_destruction_task_line: 'baris pemusnahan FG',
  wip_return_line: 'baris retur WIP',
  rm_tobacco_return_line: 'baris retur tembakau'
}

/** Nama ramah collection untuk kalimat pemakaian. */
export const usageLabel = (collection: string) => USAGE_LABEL[collection] ?? collection

export type UsageItem = { collection: string; label: string; count: number }
export type Usage = { total: number; items: UsageItem[]; sentence: string }

const rowRefs = (row: LocalRow, field: string, id: string) => idOf(row[field]) === id

/**
 * Hitung baris lain yang menunjuk ke `collection`/`id` memakai peta relasi skema local-db (`relations`: FK → collection).
 * `exclude` melewatkan collection yang bukan pemakaian bermakna (mis. `tx_log`).
 */
export function usageByRelations(collection: string, id: string, exclude: string[] = []): Usage {
  const counts = new Map<string, number>()
  for (const [child, definition] of Object.entries(LOCAL_DB_SCHEMA)) {
    if (exclude.includes(child)) continue
    for (const [field, target] of Object.entries(definition.relations ?? {})) {
      if (target !== collection) continue
      const hits = readTable(child).filter(row => rowRefs(row, field, id)).length
      if (hits) counts.set(child, (counts.get(child) ?? 0) + hits)
    }
  }
  const items = [...counts.entries()]
    .map(([child, count]) => ({ collection: child, label: USAGE_LABEL[child] ?? child, count }))
    .sort((a, b) => b.count - a.count)
  return { total: items.reduce((sum, item) => sum + item.count, 0), items, sentence: describeUsage(items) }
}

export function describeUsage(items: { label: string; count: number }[]): string {
  return items.map(item => `${item.count} ${item.label}`).join(', ')
}

/** Pemakaian dari daftar baris yang sudah dihitung pemanggil. */
export function usageFromCounts(counts: [string, number][]): Usage {
  const items = counts.filter(([, count]) => count > 0).map(([label, count]) => ({ collection: label, label, count }))
  return { total: items.reduce((sum, item) => sum + item.count, 0), items, sentence: describeUsage(items) }
}

/* ------------------------------------------------------------------ aktif / nonaktif / hapus */

export const REASON_MIN = 3
export const REASON_REQUIRED = 'Alasan wajib diisi (minimal 3 huruf).'

type StatusInput = { id: string; is_active: boolean; reason?: string }

/**
 * Aktif/nonaktif seragam (standar v7.34): menonaktifkan wajib beralasan; data tidak dihapus; mengaktifkan kembali
 * mengosongkan alasan. Mengembalikan baris terbaru.
 */
export function applyStatus(
  collection: string,
  body: StatusInput,
  options: { entity: string; label: string; page: string; wh: MasterWh | null; ref?: string }
): LocalRow {
  const row = findRow(collection, body.id)
  if (!row) fail(`${options.entity} tidak ditemukan.`, 'ERR_NOT_FOUND', 404)
  const reason = text(body.reason)
  if (!body.is_active && reason.length < REASON_MIN) fail(REASON_REQUIRED)
  const updated = updateRow(collection, row.id, {
    ...auditFieldsForUpdate(),
    is_active: body.is_active,
    inactive_reason: body.is_active ? null : reason
  }) as LocalRow
  masterLog(
    options.wh,
    options.page,
    body.is_active ? 'mst-activate' : 'mst-deactivate',
    options.ref ?? String(row.code ?? row.id),
    `${body.is_active ? 'Aktifkan Kembali' : 'Nonaktifkan'} ${options.entity} ${options.label}${reason ? ` — alasan: ${reason}` : ''}`
  )
  return updated
}

/** Hapus permanen hanya bila belum dipakai transaksi mana pun. */
export function applyDelete(
  collection: string,
  id: string,
  usage: Usage,
  options: { entity: string; label: string; page: string; wh: MasterWh | null; ref?: string }
) {
  const row = findRow(collection, id)
  if (!row) fail(`${options.entity} tidak ditemukan.`, 'ERR_NOT_FOUND', 404)
  if (usage.total > 0)
    fail(`${options.entity} masih dipakai (${usage.sentence}); tidak dapat dihapus permanen.`, 'ERR_IN_USE', 409)
  deleteRow(collection, id)
  masterLog(
    options.wh,
    options.page,
    'mst-delete',
    options.ref ?? String(row.code ?? id),
    `Hapus permanen ${options.entity} ${options.label} (belum terpakai)`
  )
}

/**
 * Import Excel master (laporan 22): peramban mengurai berkas .xlsx sungguhan, server memvalidasi tiap baris lalu menandainya
 * valid / duplikat (dilewati) / invalid (ditolak + alasan). Hanya baris valid yang disimpan lewat handler master yang sama.
 */
export type ImportState = 'valid' | 'duplicate' | 'invalid'
export type ImportCheck = { state: ImportState; reason?: string }
export type ImportRow = { n: number; cells: string[]; state: ImportState; reason?: string }
/** Muatan pratinjau / commit: `rows` = baris hasil urai berkas (kunci = judul kolom templat, `rowNumber` = baris Excel). */
export type ImportRowsBody = { rows?: Record<string, unknown>[]; file_name?: string }
export type ImportRowInput = { n: number; raw: Record<string, string> }
export const IMPORT_MAX_ROWS = 1000

export function importRowsOf(body: ImportRowsBody): ImportRowInput[] {
  const rows = Array.isArray(body.rows) ? body.rows : []
  if (!rows.length) fail('Pilih berkas Excel dulu — tidak ada baris untuk diimport.')
  if (rows.length > IMPORT_MAX_ROWS) fail(`Berkas memuat ${rows.length} baris; maksimal ${IMPORT_MAX_ROWS} per import.`)
  return rows.map((row, index) => ({
    n: Number(row.rowNumber) || index + 2,
    raw: Object.fromEntries(
      Object.entries(row)
        .filter(([key]) => key !== 'rowNumber')
        .map(([key, value]) => [key, text(value)])
    )
  }))
}

export const importFileOf = (body: ImportRowsBody, fallback: string) => text(body.file_name) || fallback

export function importSummary(file: string, rows: ImportRow[]) {
  return {
    file,
    rows,
    valid: rows.filter(row => row.state === 'valid').length,
    duplicate: rows.filter(row => row.state === 'duplicate').length,
    invalid: rows.filter(row => row.state === 'invalid').length
  }
}

/* ------------------------------------------------------------------ registri entitas (endpoint umum) */

export type MasterEntity = {
  collection: string
  /** Kata benda tunggal untuk kalimat ("Satuan", "SKU FG"). */
  entity: string
  page: string
  wh: MasterWh | null
  labelOf: (row: LocalRow) => string
  usage: (row: LocalRow) => Usage
  /** Hapus permanen diizinkan (hanya bila belum terpakai) — Satuan dan Alasan tidak pernah dihapus. */
  deletable: boolean
  /** Alasan penolakan nonaktifkan (null = boleh). */
  guardOff?: (row: LocalRow) => string | null
  /** Master yang status/penghapusannya tidak hanya satu baris (mis. BOM per SKU = semua barisnya, Area mesin = + mesin). */
  setActive?: (row: LocalRow, body: { is_active: boolean; reason?: string }) => LocalRow
  remove?: (row: LocalRow) => void
}

export const MASTER_ENTITIES: Record<string, MasterEntity> = {}
export const registerEntity = (key: string, definition: MasterEntity) => {
  MASTER_ENTITIES[key] = definition
}
const entityOf = (key: unknown): MasterEntity => {
  const definition = MASTER_ENTITIES[text(key)]
  if (!definition) fail('Jenis master tidak dikenal.', 'ERR_VALIDATION_FAILED', 400)
  return definition
}

/** Pemakaian satu baris master untuk layar Detail / modal Nonaktifkan. */
export function usageOfEntity(body: { entity?: string; id?: string }) {
  const definition = entityOf(body.entity)
  const row = findRow(definition.collection, text(body.id))
  if (!row) fail(`${definition.entity} tidak ditemukan.`, 'ERR_NOT_FOUND', 404)
  const usage = definition.usage(row)
  return { ...usage, can_delete: definition.deletable && usage.total === 0 }
}

export function setEntityStatus(body: { entity?: string; id?: string; is_active?: boolean; reason?: string }) {
  const definition = entityOf(body.entity)
  requireWrite(definition.page, `Ubah status ${definition.entity} hanya untuk peran yang berwenang.`)
  const row = findRow(definition.collection, text(body.id))
  if (!row) fail(`${definition.entity} tidak ditemukan.`, 'ERR_NOT_FOUND', 404)
  if (!body.is_active) {
    const blocked = definition.guardOff?.(row)
    if (blocked) fail(blocked, 'ERR_IN_USE', 409)
  }
  if (definition.setActive) {
    const reason = text(body.reason)
    if (!body.is_active && reason.length < REASON_MIN) fail(REASON_REQUIRED)
    return definition.setActive(row, { is_active: Boolean(body.is_active), reason })
  }
  return applyStatus(
    definition.collection,
    { id: row.id, is_active: Boolean(body.is_active), reason: body.reason },
    { entity: definition.entity, label: definition.labelOf(row), page: definition.page, wh: definition.wh }
  )
}

export function deleteEntity(body: { entity?: string; id?: string }) {
  const definition = entityOf(body.entity)
  requireWrite(definition.page, `Hapus ${definition.entity} hanya untuk peran yang berwenang.`)
  if (!definition.deletable)
    fail(`${definition.entity} tidak pernah dihapus — hanya dinonaktifkan.`, 'ERR_VALIDATION_FAILED')
  const row = findRow(definition.collection, text(body.id))
  if (!row) fail(`${definition.entity} tidak ditemukan.`, 'ERR_NOT_FOUND', 404)
  if (definition.remove) {
    const usage = definition.usage(row)
    if (usage.total > 0)
      fail(`${definition.entity} masih dipakai (${usage.sentence}); tidak dapat dihapus permanen.`, 'ERR_IN_USE', 409)
    definition.remove(row)
    return { id: row.id }
  }
  applyDelete(definition.collection, row.id, definition.usage(row), {
    entity: definition.entity,
    label: definition.labelOf(row),
    page: definition.page,
    wh: definition.wh
  })
  return { id: row.id }
}
